import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Divider,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import {
    Payment as PaymentIcon,
    AccountBalance as BankIcon,
    CreditCard as CardIcon,
    Receipt as ReceiptIcon,
    Security as SecurityIcon,
    CheckCircle as CheckIcon,
    Warning as WarningIcon,
    Visibility as ViewIcon,
    ArrowBack as BackIcon,
    Assessment as AssessmentIcon,
    CloudUpload as OfflineIcon
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import authService from '../../services/authService';
import laboratoryService from '../../services/laboratoryService';
import feesService from '../../services/feesService';
import paymentService from '../../services/paymentService';
import { decryptText } from '../../utils/encryption';

const AssessmentPaymentPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Get encrypted parameters from URL
    const encryptedAppIdFromURL = searchParams.get('appId');
    const encryptedOrgIdFromURL = searchParams.get('orgId');
    const encryptedApplicationNo = searchParams.get('applicationNo');

    const appId = decryptText(encryptedAppIdFromURL) || localStorage.getItem('appId');
    const orgId = decryptText(encryptedOrgIdFromURL) || localStorage.getItem('orgId');
    const applicationNo = decryptText(encryptedApplicationNo) || localStorage.getItem('applicationNo');

    // State management
    const [paymentDetails, setPaymentDetails] = useState(null);
    const [paymentTADADetails, setPaymentTADADetails] = useState(0);
    const [activeStep, setActiveStep] = useState(0);
    const [paymentStatus, setPaymentStatus] = useState('pending');
    const [loading, setLoading] = useState(false);
    const [deductTDS, setDeductTDS] = useState(false);
    const [tanNumber, setTanNumber] = useState('');
    const [labBasicDetails, setLabBasicDetails] = useState(null);
    const [labDetailsLoading, setLabDetailsLoading] = useState(true);
    const [labDetailsError, setLabDetailsError] = useState(null);
    const [gstExemptionData, setGstExemptionData] = useState(null);
    const [isGstExempted, setIsGstExempted] = useState(false);
    // Offline Payment States
    const [showBankDetails, setShowBankDetails] = useState(false);
    const [showOfflineForm, setShowOfflineForm] = useState(false);
    const [bankDetailsType, setBankDetailsType] = useState('government'); // 'government' or 'other'
    const [offlinePaymentData, setOfflinePaymentData] = useState({
        mode: '',
        referenceNumber: '',
        transactionDate: new Date(),
        remarks: ''
    });
    const [offlineSubmissionLoading, setOfflineSubmissionLoading] = useState(false);
    const [paymentMode, setPaymentMode] = useState('online'); // 'online' or 'offline'
    const [feesLoading, setFeesLoading] = useState(true);
    const [feesError, setFeesError] = useState(null);

    const getGstExemption = async () => {
        try {

            const response = await feesService.getGstExemption(appId);

            if (response.status === 'success' && response.data && response.data.gstData) {

                const gstData = response.data.gstData;

                setGstExemptionData(gstData);

                const exempted = gstData.isapproved === 1;

                setIsGstExempted(exempted);

            } else {

                setGstExemptionData(null);
                setIsGstExempted(false);

            }

        } catch (error) {

            console.error('Error fetching GST exemption:', error);
            setGstExemptionData(null);
            setIsGstExempted(false);

        }
    };

    useEffect(() => {
        if (!appId || !orgId) {
            toast.error('Missing required parameters. Please go back to dashboard.');
            navigate('/main/dashboard');
            return;
        }

        // Fetch both basic details and assessment fees data
        fetchBasicDetails();
        fetchAssessmentFeesData();
        fetchAssessmentTADAFeesData()
        getGstExemption();   // ⭐ ADD THIS
        // Check if payment was recently completed
        checkRecentPayment();

        // Check URL params for payment status
        const urlParams = new URLSearchParams(window.location.search);
        const paymentSuccess = urlParams.get('payment_success');
        const orderId = urlParams.get('order_id');

        if (paymentSuccess === 'true' && orderId) {
            setPaymentStatus('success');
            setActiveStep(2);
            console.log('Payment success detected from URL params');
        } else if (paymentSuccess === 'false') {
            setPaymentStatus('failed');
            console.log('Payment failure detected from URL params');
        }
    }, [appId, orgId, navigate]);

    const checkRecentPayment = async () => {
        try {
            const paymentCompletion = await paymentService.checkPaymentCompletion();
            if (paymentCompletion.isCompleted) {
                setPaymentStatus('success');
                setActiveStep(2);
                console.log('Recent successful payment found:', paymentCompletion.paymentDetails);
            }
        } catch (error) {
            console.error('Error checking recent payment:', error);
        }
    };

    const fetchAssessmentFeesData = async () => {
        try {
            setFeesLoading(true);
            setFeesError(null);

            // For assessment fees, we'll get the data from DynamicInvoiceTable
            const response = await feesService.getAssessmentFeesByAppId(appId);
            console.log('Assessment Fees API Response:', response);

            if (response.success && response.data) {
                let summary = response.data.summary || {
                    grossTotal: 0,
                    gstAmount: 0,
                    gstRate: 18,
                    grandTotal: 0
                };
                if (isGstExempted) {
                    summary = {
                        ...summary,
                        gstAmount: 0,
                        grandTotal: summary.grossTotal
                    };
                }
                setPaymentDetails({
                    appId: response.data.appId || appId,
                    invoiceData: response.data.invoiceData || [],
                    summary: summary,
                    paymentType: 'assessment'
                });

                console.log('Assessment fees data loaded from API:', response.data);
            } else {
                // Fallback to mock data for development
                const mockAssessmentFees = {};

                setPaymentDetails(mockAssessmentFees);
                console.log('Using mock assessment fees data:', mockAssessmentFees);
            }
        } catch (error) {
            console.error('Error fetching assessment fees data:', error);
            setFeesError(error.message);

            // Use fallback data even on error
            const fallbackFees = {
                appId: appId,
                invoiceData: [],
                summary: {},
                paymentType: 'assessment'
            };
            setPaymentDetails(fallbackFees);
        } finally {
            setFeesLoading(false);
        }
    };
    const fetchAssessmentTADAFeesData = async () => {
        try {
            setFeesLoading(true);
            setFeesError(null);

            // For assessment fees, we'll get the data from DynamicInvoiceTable
            const response = await feesService.getAssessmentTADAFeesByAppId(appId);
            console.log('Assessment Fees API Response:', response);

            if (response.success && response.data) {
                setPaymentTADADetails({
                    appId: response.data.appId || appId,
                    tadaAmount: response.data.tadaAmount || 0,
                    tadaPath: response.data.tadaPath || ''

                });

                console.log('Assessment fees data loaded from API:', response.data);
            } else {                // Fallback to mock data for development
                console.log('Using mock assessment fees data');
            }
        } catch (error) {
            console.error('Error fetching assessment fees data:', error);
            setFeesError(error.message);

            // Use fallback data even on error
            const fallbackFees = {
                appId: appId,
                invoiceData: [],
                summary: {},
                paymentType: 'assessment'
            };
            setPaymentDetails(fallbackFees);
        } finally {
            setFeesLoading(false);
        }
    };

    const fetchBasicDetails = async () => {
        try {
            setLabDetailsLoading(true);
            setLabDetailsError(null);

            const response = await laboratoryService.getPaymentBasicDetail(appId);

            if (response.success && response.data) {
                setLabBasicDetails({
                    LaboratoryName: response.data.CabName || response.data.LaboratoryName || 'N/A',
                    ContactEmail: response.data.ContactEmail || response.data.Email || 'N/A',
                    ContactMobile: response.data.ContactPersonMobileNo || 'N/A',
                    OrgId: response.data.OrgId || orgId,
                    AppId: response.data.AppId || appId,
                    Address: response.data.Address || response.data.AddressLine1 || 'N/A',
                    City: response.data.City || 'N/A',
                    State: response.data.State || 'N/A',
                    LaboratoryType: 'Assessment Laboratory',
                    GSTNumber: response.data.GSTNumber || '',
                    ContactPerson: response.data.ContactPerson || '',
                    TAN: response.data.TAN || '',
                    Pincode: response.data.Pincode || response.data.Pin || '',
                    LegalIdentityType: response.data.LegalIdentityType || '',
                });
                console.log('Basic details loaded from API:', response.data);
            } else {
                throw new Error(response.message || 'Failed to fetch basic details');
            }
        } catch (error) {
            console.error('Error fetching basic details:', error);
            setLabDetailsError(error.message);

            // Fallback to mock data if API fails
            setLabBasicDetails({
                LaboratoryName: 'Sample Assessment Laboratory',
                ContactEmail: 'assessment@lab.com',
                ContactMobile: '9999999999',
                OrgId: orgId,
                AppId: appId,
                Address: 'Sample Address',
                City: 'New Delhi',
                State: 'Delhi',
                LaboratoryType: 'Assessment Laboratory',
                GSTNumber: '',
                ContactPerson: 'Assessment Contact',
                TAN: '',
                Pincode: '110001'
            });
        } finally {
            setLabDetailsLoading(false);
        }
    };
    useEffect(() => {

        if (paymentDetails && paymentDetails.summary) {

            let updatedSummary = { ...paymentDetails.summary };

            if (isGstExempted) {

                updatedSummary = {
                    ...updatedSummary,
                    gstAmount: 0,
                    grandTotal: updatedSummary.grossTotal
                };

            }

            setPaymentDetails(prev => ({
                ...prev,
                summary: updatedSummary
            }));
        }

    }, [isGstExempted]);

    const calculateTDSAmount = () => {
        if (!paymentDetails || !deductTDS) return 0;
        const assessmentFees =
            (paymentDetails?.summary?.grossTotal || 0) -
            (paymentDetails?.summary?.gstAmount || 0) -
            (paymentTADADetails?.tadaAmount || 0);

        return Math.round(assessmentFees * 0.02); // 2% TDS
    };

    const handlePayment = async () => {
        if (!paymentDetails || !labBasicDetails) {
            toast.error('Payment details not available');
            return;
        }

        if (deductTDS && !tanNumber.trim()) {
            toast.error('TAN number is required for TDS deduction');
            return;
        }

        try {
            setLoading(true);
            setPaymentStatus('processing');

            const userSession = authService.getUserData();
            if (!userSession) {
                throw new Error('User session not found. Please login again.');
            }

            // Prepare payment data matching the working Medical PaymentPage pattern
            const finalAmount = deductTDS ? (paymentDetails.summary.grandTotal - calculateTDSAmount()) : paymentDetails.summary.grandTotal;

            const apiPaymentData = {
                appId: labBasicDetails?.AppId || paymentDetails.appId,
                orgId: userSession.orgId || labBasicDetails?.OrgId || authService.getOrgId(),
                paymentTypeId: '6', // Assessment payment type
                amount: finalAmount - (paymentDetails?.summary?.gstAmount || 0) + (deductTDS ? calculateTDSAmount() : 0),
                gstAmount: paymentDetails?.summary?.gstAmount || 0,
                totalAmount: finalAmount,
                currency: 'INR',
                email: labBasicDetails?.ContactEmail || userSession.email,
                mobile: labBasicDetails?.ContactMobile || userSession.mobile,
                labTypeId: '1', // Default lab type
                chkDeductTds: deductTDS,
                TDSAmount: deductTDS ? calculateTDSAmount() : 0,
                TDSPercent: deductTDS ? 2 : 0,
                tan: tanNumber,
                userName: authService.getUsername() || userSession.username || localStorage.getItem('username')
            };

            console.log('Assessment Payment API Data:', apiPaymentData);

            // Use the payment service to initiate ICICI payment
            const result = await paymentService.initiateICICIPaymentAssessment(apiPaymentData);

            if (result.success && result.data && result.data.paymentUrl) {
                console.log('ICICI EasyPay URL generated for assessment, redirecting...');

                // Store payment information for tracking
                localStorage.setItem('currentOrderId', result.data.orderId);
                localStorage.setItem('paymentInitiated', JSON.stringify({
                    orderId: result.data.orderId,
                    amount: result.data.amount,
                    appId: apiPaymentData.appId,
                    orgId: apiPaymentData.orgId,
                    paymentType: 'assessment',
                    timestamp: new Date().toISOString()
                }));

                // Show success message before redirect
                setPaymentStatus('redirecting');

                // Small delay to show the status, then redirect
                setTimeout(() => {
                    window.location.href = result.data.paymentUrl;
                }, 1000);

                return;
            } else {
                throw new Error(result.message || 'Failed to generate payment URL from server');
            }

        } catch (error) {
            console.error('Assessment payment processing error:', error);
            setPaymentStatus('failed');

            let errorMessage = `Assessment payment processing failed: ${error.message}`;

            if (error.message.includes('session')) {
                errorMessage = 'Your session has expired. Please login again.';
            } else if (error.message.includes('TAN')) {
                errorMessage = 'TAN number is required for TDS deduction.';
            }

            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Offline Payment Handlers
    const handleOfflinePaymentSubmit = async () => {
        try {
            if (!offlinePaymentData.mode || !offlinePaymentData.referenceNumber) {
                toast.error('Please fill all required fields');
                return;
            }

            setOfflineSubmissionLoading(true);

            const offlineData = {
                orgId: orgId,
                appId: appId,
                paymentTypeId: '6', // Assessment fee payment type
                mode: offlinePaymentData.mode,
                referenceNumber: offlinePaymentData.referenceNumber,
                amount: paymentDetails?.summary?.grandTotal - (deductTDS ? calculateTDSAmount() : 0),
                remarks: offlinePaymentData.remarks,
                transactionDate: offlinePaymentData.transactionDate.toISOString()
            };

            console.log('Submitting offline assessment payment:', offlineData);

            const response = await paymentService.submitOfflinePayment(offlineData);

            if (response.success) {
                toast.success('Offline payment details submitted successfully! Payment will be updated within 24 hours (Working days).');
                setShowOfflineForm(false);
                // Reset form
                setOfflinePaymentData({
                    mode: '',
                    referenceNumber: '',
                    transactionDate: new Date(),
                    remarks: ''
                });
                // Optionally redirect to dashboard
                setTimeout(() => {
                    navigate('/main/dashboard');
                }, 2000);
            } else {
                toast.error(response.message || 'Failed to submit offline payment details');
            }

        } catch (error) {
            console.error('Error submitting offline payment:', error);
            toast.error('Failed to submit offline payment details. Please try again.');
        } finally {
            setOfflineSubmissionLoading(false);
        }
    };

    const getVirtualAccountNumber = () => {
        if (!orgId || !appId) return '';

        const username = localStorage.getItem("username") || "";
        const cleanedUsername = username.replace(/^(nabln|nabl)/, '');

        return `NABLQC${cleanedUsername}`;
    };

    const steps = [
        'Review Assessment Payment Details',
        'Verify & Pay',
        'Payment Confirmation'
    ];

    const handlePerformaInvoice = () => {
        const previewUrl = `https://nablwpnew.qci.org.in/UnpaidInvoiceAssessment.aspx?appId=${appId}&payType=6&OrgId=${orgId}&id=${paymentDetails?.invoiceData?.[0]?.id}`;
        window.open(previewUrl, '_blank');
    };
    const handlePerformaInvoiceTADA = (tadaPath) => {
        const previewUrl = tadaPath
        window.open(previewUrl, '_blank');
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    if (labDetailsLoading || feesLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <Typography variant="h6">Loading assessment payment details...</Typography>
            </Box>
        );
    }

    if (labDetailsError || feesError) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    <Typography variant="h6">Error Loading Payment Details</Typography>
                    <Typography variant="body2">
                        {labDetailsError || feesError}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                        <Button variant="outlined" onClick={() => navigate('/main/dashboard')}>
                            Back to Dashboard
                        </Button>
                        <Button variant="contained" sx={{ ml: 2 }} onClick={() => window.location.reload()}>
                            Retry
                        </Button>
                    </Box>
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 3, p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
            {/* Header */}
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <AssessmentIcon color="primary" sx={{ fontSize: 40 }} />
                        <Box>
                            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                Assessment Fees Payment
                            </Typography>
                            <Typography variant="subtitle1" color="text.secondary">
                                Complete your assessment fees payment securely
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={<BackIcon />}
                        onClick={() => navigate('/main/dashboard')}
                    >
                        Back to Dashboard
                    </Button>
                </Box>
            </Paper>

            {/* Main Content */}
            <Grid container spacing={3}>
                {/* Left Column - Payment Details */}
                <Grid item xs={12} md={8}>
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Stepper activeStep={activeStep} orientation="vertical">
                            {/* Step 1: Review Payment Details */}
                            <Step>
                                <StepLabel>
                                    <Typography variant="h6">Review Assessment Payment Details</Typography>
                                </StepLabel>
                                <StepContent>
                                    {/* Laboratory Information */}
                                    <Card sx={{ mb: 3, backgroundColor: '#f8f9fa' }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <AssessmentIcon />
                                                Laboratory Information
                                            </Typography>
                                            <Divider sx={{ mb: 2 }} />
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2" color="text.secondary">Laboratory Name</Typography>
                                                    <Typography variant="body1" fontWeight={500}>{labBasicDetails?.LaboratoryName}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2" color="text.secondary">Application ID</Typography>
                                                    <Typography variant="body1" fontWeight={500}>{applicationNo}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2" color="text.secondary">Contact Email</Typography>
                                                    <Typography variant="body1" fontWeight={500}>{labBasicDetails?.ContactEmail}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2" color="text.secondary">Contact Mobile</Typography>
                                                    <Typography variant="body1" fontWeight={500}>{labBasicDetails?.ContactMobile}</Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography variant="body2" color="text.secondary">Address</Typography>
                                                    <Typography variant="body1" fontWeight={500}>
                                                        {labBasicDetails?.Address}, {labBasicDetails?.City}, {labBasicDetails?.State} - {labBasicDetails?.Pincode}
                                                    </Typography>
                                                </Grid>
                                                {labBasicDetails?.GSTNumber && (
                                                    <Grid item xs={12} sm={6}>
                                                        <Typography variant="body2" color="text.secondary">GST Number</Typography>
                                                        <Typography variant="body1" fontWeight={500}>{labBasicDetails.GSTNumber}</Typography>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </CardContent>
                                    </Card>

                                    {/* Assessment Fees Breakdown */}
                                    <Card sx={{ mb: 3 }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <ReceiptIcon />
                                                Assessment Fees Breakdown
                                            </Typography>
                                            <Divider sx={{ mb: 2 }} />
                                            {isGstExempted && (
                                                <Alert severity="success" sx={{ mb: 2 }}>
                                                    GST Exemption Applied - No GST will be charged
                                                </Alert>
                                            )}
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {/* {paymentDetails?.invoiceData?.map((item, index) => (
                                                            <TableRow key={index}>
                                                                <TableCell>{item.description}</TableCell>
                                                                <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                                                            </TableRow>
                                                        ))} */}
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 600 }}>Assessment Fees</TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                                {formatCurrency((paymentDetails?.summary?.grossTotal || 0) - (paymentDetails?.summary?.gstAmount || 0) - (paymentTADADetails.tadaAmount || 0))}
                                                            </TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 600 }}>Assessment TADA Fees</TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                                {formatCurrency((paymentTADADetails.tadaAmount || 0))}
                                                            </TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell>
                                                                GST (18%)
                                                                {isGstExempted && (
                                                                    <Chip
                                                                        label="EXEMPTED"
                                                                        size="small"
                                                                        color="success"
                                                                        sx={{ ml: 1 }}
                                                                    />
                                                                )}
                                                            </TableCell>

                                                            <TableCell align="right">
                                                                {formatCurrency(paymentDetails?.summary?.gstAmount || 0)}
                                                            </TableCell>
                                                        </TableRow>
                                                        {deductTDS && (
                                                            <TableRow>
                                                                <TableCell>TDS Deduction (2%)</TableCell>
                                                                <TableCell align="right" sx={{ color: 'error.main' }}>
                                                                    -{formatCurrency(calculateTDSAmount())}
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: '1.1em' }}>
                                                                Total Amount
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1.1em', color: 'primary.main' }}>
                                                                {formatCurrency((paymentDetails?.summary?.grandTotal || 0) - (deductTDS ? calculateTDSAmount() : 0))}
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </CardContent>
                                    </Card>

                                    <Box sx={{ mb: 2 }}>
                                        <Button
                                            variant="contained"
                                            onClick={() => setActiveStep(1)}
                                            disabled={!paymentDetails}
                                        >
                                            Continue to Payment
                                        </Button>
                                    </Box>
                                </StepContent>
                            </Step>

                            {/* Step 2: Payment Options */}
                            <Step>
                                <StepLabel>
                                    <Typography variant="h6">Verify & Pay</Typography>
                                </StepLabel>
                                <StepContent>
                                    {/* TDS Options */}
                                    <Card sx={{ mb: 3 }}>
                                        <CardContent>
                                            {/* <Typography variant="h6" gutterBottom>Tax Deduction Options</Typography> */}
                                            {/* <Typography variant="h6" gutterBottom>No TDS Deduction Applicable</Typography> */}
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={deductTDS}
                                                        onChange={(e) => setDeductTDS(e.target.checked)}
                                                    />
                                                }
                                                label="Deduct TDS (Tax Deducted at Source) - 2%"
                                            />

                                            {deductTDS && (
                                                <Box sx={{ mt: 2 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="TAN Number"
                                                        value={tanNumber}
                                                        onChange={(e) => setTanNumber(e.target.value.toUpperCase())}
                                                        placeholder="Enter your TAN number"
                                                        helperText="TAN (Tax Deduction Account Number) is mandatory for TDS deduction"
                                                        required={deductTDS}
                                                    />
                                                </Box>
                                            )}
                                        </CardContent>
                                    </Card>

                                    {/* Payment Summary */}
                                    <Card sx={{ mb: 3, backgroundColor: '#e3f2fd' }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>Payment Summary</Typography>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                <Typography>Assessment Fees (Excluding GST):</Typography>
                                                <Typography fontWeight={500}>
                                                    {formatCurrency(
                                                        ((paymentDetails?.summary?.grossTotal || 0) -
                                                            (paymentDetails?.summary?.gstAmount || 0) -
                                                            (paymentTADADetails?.tadaAmount || 0))
                                                    )}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                <Typography>Assessment TADA Fees:</Typography>
                                                <Typography fontWeight={500}>
                                                    {formatCurrency(paymentTADADetails?.tadaAmount || 0)}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                <Typography>GST ({18}%):</Typography>
                                                <Typography fontWeight={500}>
                                                    {formatCurrency(paymentDetails?.summary?.gstAmount || 0)}
                                                </Typography>
                                            </Box>
                                            {deductTDS && (
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography>TDS Deduction (2%):</Typography>
                                                    <Typography fontWeight={500} sx={{ color: 'error.main' }}>
                                                        -{formatCurrency(calculateTDSAmount())}
                                                    </Typography>
                                                </Box>
                                            )}
                                            <Divider sx={{ my: 1 }} />
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="h6">Total Payable:</Typography>
                                                <Typography variant="h6" color="primary.main" fontWeight={600}>
                                                    {formatCurrency((paymentDetails?.summary?.grandTotal || 0) - (deductTDS ? calculateTDSAmount() : 0))}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>

                                    {/* Payment Processing Status */}
                                    {paymentStatus === 'processing' && (
                                        <Alert severity="warning" sx={{ mb: 3 }}>
                                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                Connecting to ICICI Payment Gateway...
                                            </Typography>
                                            <Typography variant="body2">
                                                Please wait while we process your payment request and prepare the secure gateway connection.
                                            </Typography>
                                        </Alert>
                                    )}

                                    {paymentStatus === 'redirecting' && (
                                        <Alert severity="success" sx={{ mb: 3 }}>
                                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                Redirecting to ICICI EasyPay Gateway...
                                            </Typography>
                                            <Typography variant="body2">
                                                Payment URL generated successfully. You will be redirected to the secure payment gateway in a moment.
                                                Do not close this window or press the back button.
                                            </Typography>
                                        </Alert>
                                    )}

                                    {/* Payment Gateway Info */}
                                    <Alert severity="info" sx={{ mb: 3 }}>
                                        <Typography variant="body2">
                                            <SecurityIcon sx={{ fontSize: 16, mr: 1 }} />
                                            Your payment will be processed securely through ICICI Bank Payment Gateway.
                                            You will be redirected to the bank's secure payment page.
                                        </Typography>
                                    </Alert>

                                    {/* Payment Mode Selection */}

                                    <Card sx={{ mb: 3 }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>Payment Mode</Typography>
                                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                                <Button
                                                    variant={paymentMode === 'online' ? 'contained' : 'outlined'}
                                                    onClick={() => setPaymentMode('online')}
                                                    startIcon={<PaymentIcon />}
                                                >
                                                    Online Payment
                                                </Button>
                                                {labBasicDetails.LegalIdentityType === 'Government' && (
                                                    <Button
                                                        variant={paymentMode === 'offline' ? 'contained' : 'outlined'}
                                                        onClick={() => setPaymentMode('offline')}
                                                        startIcon={<BankIcon />}
                                                    >
                                                        Offline Payment
                                                    </Button>
                                                )}
                                            </Box>

                                            {paymentMode === 'offline' && (
                                                <Alert severity="info" sx={{ mt: 2 }}>
                                                    <Typography variant="body2">
                                                        For offline payment, you can make payment through bank transfer and then update the transaction details.
                                                    </Typography>
                                                </Alert>
                                            )}
                                        </CardContent>
                                    </Card>

                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Button
                                            variant="outlined"
                                            onClick={() => setActiveStep(0)}
                                        >
                                            Back
                                        </Button>

                                        {paymentMode === 'online' ? (
                                            <Button
                                                variant="contained"
                                                onClick={handlePayment}
                                                disabled={loading || paymentStatus === 'processing' || paymentStatus === 'redirecting' || (deductTDS && !tanNumber.trim())}
                                                startIcon={loading || paymentStatus === 'processing' || paymentStatus === 'redirecting' ? null : <PaymentIcon />}
                                                sx={{
                                                    minWidth: 200,
                                                    backgroundColor: paymentStatus === 'processing' ? 'warning.main' :
                                                        paymentStatus === 'redirecting' ? 'success.main' : 'primary.main'
                                                }}
                                            >
                                                {loading ? 'Connecting to ICICI Gateway...' :
                                                    paymentStatus === 'processing' ? 'Processing Payment Request...' :
                                                        paymentStatus === 'redirecting' ? 'Redirecting to EasyPay...' :
                                                            `Pay ${formatCurrency((paymentDetails?.summary?.grandTotal || 0) - (deductTDS ? calculateTDSAmount() : 0))} via ICICI`
                                                }
                                            </Button>
                                        ) : (
                                            <>
                                                <Button
                                                    variant="contained"
                                                    onClick={() => setShowBankDetails(true)}
                                                    startIcon={<BankIcon />}
                                                    color="info"
                                                >
                                                    Get Bank Details
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    onClick={() => setShowOfflineForm(true)}
                                                    startIcon={<OfflineIcon />}
                                                    color="success"
                                                >
                                                    Submit Payment Details
                                                </Button>
                                            </>
                                        )}
                                    </Box>
                                </StepContent>
                            </Step>

                            {/* Step 3: Payment Confirmation */}
                            <Step>
                                <StepLabel>
                                    <Typography variant="h6">Payment Confirmation</Typography>
                                </StepLabel>
                                <StepContent>
                                    {paymentStatus === 'success' ? (
                                        <Alert severity="success">
                                            <Typography variant="h6" gutterBottom>
                                                <CheckIcon sx={{ mr: 1 }} />
                                                Assessment Payment Successful!
                                            </Typography>
                                            <Typography variant="body2">
                                                Your assessment fees payment has been processed successfully.
                                                You will receive a confirmation email shortly.
                                            </Typography>
                                        </Alert>
                                    ) : paymentStatus === 'failed' ? (
                                        <Alert severity="error">
                                            <Typography variant="h6" gutterBottom>
                                                <WarningIcon sx={{ mr: 1 }} />
                                                Payment Failed
                                            </Typography>
                                            <Typography variant="body2">
                                                Your assessment payment could not be processed. Please try again.
                                            </Typography>
                                        </Alert>
                                    ) : (
                                        <Alert severity="info">
                                            <Typography variant="body2">
                                                Payment status will be updated here...
                                            </Typography>
                                        </Alert>
                                    )}
                                </StepContent>
                            </Step>
                        </Stepper>
                    </Paper>
                </Grid>

                {/* Right Column - Additional Information */}
                <Grid item xs={12} md={4}>
                    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            <ReceiptIcon sx={{ mr: 1 }} />
                            Quick Actions
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<ViewIcon />}
                                onClick={handlePerformaInvoice}
                                fullWidth
                            >
                                View Proforma Invoice
                            </Button>
                            {paymentTADADetails.tadaAmount > 0 && (
                                <Button
                                    variant="outlined"
                                    startIcon={<ViewIcon />}
                                    onClick={() => handlePerformaInvoiceTADA(paymentTADADetails.tadaPath)}
                                    fullWidth
                                >
                                    View TA DA Proforma Invoice
                                </Button>
                            )}

                            <Button
                                variant="outlined"
                                startIcon={<BackIcon />}
                                onClick={() => navigate('/main/dashboard')}
                                fullWidth
                            >
                                Back to Dashboard
                            </Button>
                        </Box>
                    </Paper>

                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            <SecurityIcon sx={{ mr: 1 }} />
                            Secure Payment
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Your payment is secured by:
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                            <Typography component="li" variant="body2" gutterBottom>
                                256-bit SSL encryption
                            </Typography>
                            <Typography component="li" variant="body2" gutterBottom>
                                ICICI Bank secure gateway
                            </Typography>
                            <Typography component="li" variant="body2" gutterBottom>
                                PCI DSS compliance
                            </Typography>
                            <Typography component="li" variant="body2">
                                Real-time payment verification
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Bank Details Dialog */}
            <Dialog open={showBankDetails} onClose={() => setShowBankDetails(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    <BankIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Bank Details for Offline Payment
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            If you are a government lab and wish to make payment through PFMS, please{' '}
                            <Button
                                variant="text"
                                onClick={() => setBankDetailsType('government')}
                                sx={{ textDecoration: 'underline', p: 0, minWidth: 'auto' }}
                            >
                                click here
                            </Button>
                            {' '}for bank details
                        </Typography>
                        <Typography variant="body1">
                            In case of other modes, please{' '}
                            <Button
                                variant="text"
                                onClick={() => setBankDetailsType('other')}
                                sx={{ textDecoration: 'underline', p: 0, minWidth: 'auto' }}
                            >
                                click here
                            </Button>
                            {' '}for bank details
                        </Typography>
                    </Box>

                    {bankDetailsType === 'government' && (
                        <Card sx={{ mb: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom color="primary">Government Lab - PFMS Payment Details</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}><Typography variant="body2" fontWeight="bold">Name:</Typography></Grid>
                                    <Grid item xs={6}><Typography variant="body2">Quality Council of India</Typography></Grid>
                                    <Grid item xs={6}><Typography variant="body2" fontWeight="bold">Bank Name:</Typography></Grid>
                                    <Grid item xs={6}><Typography variant="body2">ICICI Bank</Typography></Grid>
                                    <Grid item xs={6}><Typography variant="body2" fontWeight="bold">Branch:</Typography></Grid>
                                    <Grid item xs={6}><Typography variant="body2">MILLENIUM PLAZA GURGAON</Typography></Grid>
                                    <Grid item xs={6}><Typography variant="body2" fontWeight="bold">Account Number:</Typography></Grid>
                                    <Grid item xs={6}><Typography variant="body2">739301000237</Typography></Grid>
                                    <Grid item xs={6}><Typography variant="body2" fontWeight="bold">IFSC Code:</Typography></Grid>
                                    <Grid item xs={6}><Typography variant="body2">ICIC0005391</Typography></Grid>
                                    <Grid item xs={6}><Typography variant="body2" fontWeight="bold">Type of Account:</Typography></Grid>
                                    <Grid item xs={6}><Typography variant="body2">Saving Account</Typography></Grid>
                                </Grid>
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    After making payment, UTR details shall be entered at the portal using offline payment functionality given at your dashboard to get the payment updated in your account.
                                </Alert>
                            </CardContent>
                        </Card>
                    )}

                    {bankDetailsType === 'other' && (
                        <Card sx={{ mb: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom color="primary">Other Payment Modes - Bank Details</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}><Typography variant="body2" fontWeight="bold">Beneficiary Name:</Typography></Grid>
                                    <Grid item xs={6}><Typography variant="body2">Quality Council of India</Typography></Grid>
                                    <Grid item xs={6}><Typography variant="body2" fontWeight="bold">Bank Name:</Typography></Grid>
                                    <Grid item xs={6}><Typography variant="body2">HDFC Bank</Typography></Grid>
                                    <Grid item xs={6}><Typography variant="body2" fontWeight="bold">Branch:</Typography></Grid>
                                    <Grid item xs={6}><Typography variant="body2">Kanjurmarg Branch, Mumbai</Typography></Grid>
                                    <Grid item xs={6}><Typography variant="body2" fontWeight="bold">IFSC Code:</Typography></Grid>
                                    <Grid item xs={6}><Typography variant="body2">HDFC0004989</Typography></Grid>
                                    <Grid item xs={6}><Typography variant="body2" fontWeight="bold">Virtual Account Number:</Typography></Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" fontWeight="bold" color="primary">
                                            {getVirtualAccountNumber()}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}><Typography variant="body2" fontWeight="bold">Type of Account:</Typography></Grid>
                                    <Grid item xs={6}><Typography variant="body2">Saving Account</Typography></Grid>
                                </Grid>
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    After making payment, Virtual Account number shall be entered at the portal using offline payment functionality given at your dashboard to get the payment updated in your account.
                                </Alert>
                            </CardContent>
                        </Card>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowBankDetails(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Offline Payment Form Dialog */}
            <Dialog open={showOfflineForm} onClose={() => setShowOfflineForm(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <OfflineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Offline Payment Details
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel>Select Mode *</InputLabel>
                            <Select
                                value={offlinePaymentData.mode}
                                label="Select Mode *"
                                onChange={(e) => setOfflinePaymentData(prev => ({ ...prev, mode: e.target.value }))}
                            >
                                <MenuItem value="">--Select--</MenuItem>
                                <MenuItem value="HDFC SmartHub">HDFC SmartHub</MenuItem>
                                <MenuItem value="Virtual Account">Virtual Account</MenuItem>
                                <MenuItem value="Direct NEFT/RTGS">Direct NEFT/RTGS</MenuItem>
                            </Select>
                        </FormControl>

                        {offlinePaymentData.mode && (
                            <TextField
                                fullWidth
                                label={
                                    offlinePaymentData.mode === 'HDFC SmartHub' ? 'HDFC Payment ID *' :
                                        offlinePaymentData.mode === 'Virtual Account' ? 'Virtual Account Number *' :
                                            offlinePaymentData.mode === 'Direct NEFT/RTGS' ? 'UTR Number *' :
                                                'Reference Number *'
                                }
                                value={offlinePaymentData.referenceNumber}
                                onChange={(e) => setOfflinePaymentData(prev => ({ ...prev, referenceNumber: e.target.value }))}
                                sx={{ mb: 3 }}
                                required
                            />
                        )}

                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label="Transaction Date *"
                                value={offlinePaymentData.transactionDate}
                                onChange={(newDate) => setOfflinePaymentData(prev => ({ ...prev, transactionDate: newDate }))}
                                renderInput={(params) => <TextField {...params} fullWidth sx={{ mb: 3 }} required />}
                            />
                        </LocalizationProvider>

                        <TextField
                            fullWidth
                            label="Remarks"
                            multiline
                            rows={3}
                            value={offlinePaymentData.remarks}
                            onChange={(e) => setOfflinePaymentData(prev => ({ ...prev, remarks: e.target.value }))}
                            sx={{ mb: 3 }}
                        />

                        <Alert severity="success" sx={{ mb: 2 }}>
                            Payment will be updated within 24 hours (Working days).
                        </Alert>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowOfflineForm(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleOfflinePaymentSubmit}
                        disabled={offlineSubmissionLoading}
                    >
                        {offlineSubmissionLoading ? 'Submitting...' : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AssessmentPaymentPage;